import axios from "axios";
import { DateTime } from "luxon";
import { toast } from "react-toastify";

// Fetch app details by App ID
export const fetchAppDetailsByAppId = async (appId) => {
  if (!appId || appId.length < 2) return null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/appDetailsByAppId?app_id=${appId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching app details by app ID:", error);
    if (error.response?.status !== 404) {
      toast.error("Failed to fetch app details", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    return null;
  }
};

// Fetch app details by App Name
export const fetchAppDetailsByAppName = async (appName) => {
  if (!appName || appName.length < 2) return null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/appDetailsByAppName?app_name=${encodeURIComponent(appName)}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching app details by app name:", error);
    if (error.response?.status !== 404) {
      toast.error("Failed to fetch app details by name", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    return null;
  }
};

// Fetch incident details
export const fetchIncidentDetails = async (incidentId) => {
  if (!incidentId || incidentId.length < 3) return null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/incidentDetails?incident_id=${incidentId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );

    if (response.data?.status === "success" && response.data?.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching incident details:", error);
    if (error.response?.status !== 404) {
      toast.error("Failed to fetch incident details", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    return null;
  }
};

// Submit new application
export const submitNewApplication = async (formData, userEmail) => {
  const payload = {
    app_id: formData.app_id,
    app_name: formData.app_name,
    platform: formData.platform,
    portfolio: formData.portfolio,
    status: formData.status,
    trigger_type: formData.trigger_type,
    incident: formData.incident,
    priority: formData.priority,
    application_representative: formData.application_representative,
    comments: formData.comments,
    added_to_red_channel: DateTime.isDateTime(formData.added_to_red_channel)
      ? formData.added_to_red_channel.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.added_to_red_channel).toFormat("yyyy-MM-dd HH:mm:ss"),
    incident_dates: formData.incident_dates,
    first_review: DateTime.isDateTime(formData.first_review)
      ? formData.first_review.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.first_review).toFormat("yyyy-MM-dd HH:mm:ss"),
    final_review: DateTime.isDateTime(formData.final_review)
      ? formData.final_review.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.final_review).toFormat("yyyy-MM-dd HH:mm:ss"),
    deployment_type: formData.deployment_type,
  };

  if (formData.status === "Green" && formData.moved_to_green_channel_on) {
    payload.moved_to_green_channel_on = DateTime.isDateTime(formData.moved_to_green_channel_on)
      ? formData.moved_to_green_channel_on.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.moved_to_green_channel_on).toFormat("yyyy-MM-dd HH:mm:ss");
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannel?user_email=${userEmail}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update existing application
export const updateApplication = async (formData, userEmail, originalAppName, originalIncidentId) => {
  const payload = {
    app_id: formData.app_id,
    app_name: formData.app_name,
    platform: formData.platform,
    portfolio: formData.portfolio,
    status: formData.status,
    trigger_type: formData.trigger_type,
    incident: formData.incident,
    priority: formData.priority,
    application_representative: formData.application_representative,
    comments: formData.comments,
    added_to_red_channel: DateTime.isDateTime(formData.added_to_red_channel)
      ? formData.added_to_red_channel.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.added_to_red_channel).toFormat("yyyy-MM-dd HH:mm:ss"),
    incident_dates: formData.incident_dates,
    first_review: DateTime.isDateTime(formData.first_review)
      ? formData.first_review.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.first_review).toFormat("yyyy-MM-dd HH:mm:ss"),
    final_review: DateTime.isDateTime(formData.final_review)
      ? formData.final_review.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.final_review).toFormat("yyyy-MM-dd HH:mm:ss"),
    deployment_type: formData.deployment_type,
  };

  if (formData.status === "Green" && formData.moved_to_green_channel_on) {
    payload.moved_to_green_channel_on = DateTime.isDateTime(formData.moved_to_green_channel_on)
      ? formData.moved_to_green_channel_on.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.moved_to_green_channel_on).toFormat("yyyy-MM-dd HH:mm:ss");
  }

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannel?user_email=${userEmail}&app_name=${encodeURIComponent(originalAppName)}&incident_id=${originalIncidentId}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Validate mandatory fields
export const validateMandatoryFields = (formData) => {
  const mandatoryFields = {
    app_name: "App Name",
    platform: "Platform",
    portfolio: "Portfolio",
    status: "Status",
    trigger_type: "Trigger",
    incident: "Incident ID",
    priority: "Priority",
    added_to_red_channel: "Added to Red Channel",
    first_review: "First Review Date",
    final_review: "Final Review Date",
  };

  const missingFields = [];
  for (const [key, label] of Object.entries(mandatoryFields)) {
    if (!formData[key]) {
      missingFields.push(label);
    }
  }

  return missingFields;
};

// Fetch statistics data
export const fetchStatisticsData = async (appId, appName, incidentDate) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/applicationStatistics`,
      {
        params: { app_id: appId, app_name: appName, incident_date: incidentDate },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );

    // Transform response to match expected structure
    const data = response.data;
    return {
      statisticsData: data.application_changes || null,
      incidentCounts: data.incident_counts || null,
    };
  } catch (error) {
    console.error("Error fetching statistics:", error);
    toast.error("Failed to fetch statistics data");
    throw error;
  }
};
